// Personalization tokens for the per-deal buyer message.
//
// Clozers substitutes these itself, PER BUYER, at send time
// (functions/createGHLOpportunity.js) — NOT via GHL merge fields — so they
// resolve reliably in both SMS and email. (A GHL merge tag stored inside a
// custom field is not re-parsed when the workflow inserts the field, so
// {{contact.first_name}} would arrive literally; our own tokens sidestep that.)
//
// The composer uses these for the "Insert" buttons and the live preview.
//
// KEEP IN SYNC with functions/utils/personalizeMessage.js (separate package —
// src/ cannot be imported by functions/, so the substitution is duplicated).

import { pickFeaturedImage, pickPortfolioImage } from './buildOpportunityCustomFields';

export const MESSAGE_TOKENS = [
    { label: 'First name', token: '{{first_name}}' },
    { label: 'Last name', token: '{{last_name}}' },
    { label: 'Street address', token: '{{street_address}}' },
    // Short, casual street handle — no house number, unit, directional, or suffix
    // (e.g. "Osborne", "Crooked Creek", "7th"). Reads naturally in a text/subject
    // ("New deal on Crooked Creek!"). From the deal's colloquialStreet; blank when
    // absent (LLM-derived on ingest, no deterministic fallback) or for a package.
    { label: 'Street name', token: '{{street_name}}' },
    // 5-digit ZIP from the deal's address (blank for a package, whose members can
    // span ZIPs). Plain value, e.g. "85042".
    { label: 'Zip', token: '{{zip}}' },
    { label: 'Price', token: '{{price}}' },
    // Beds/baths/size/year from the deal's verified characteristics (blank for a
    // package, whose members differ). Plain values, e.g. "3" / "2.5" / "1,850" / "1998".
    { label: 'Beds', token: '{{beds}}' },
    { label: 'Baths', token: '{{baths}}' },
    { label: 'Sq ft', token: '{{sqft}}' },
    { label: 'Year built', token: '{{year_built}}' },
    // NOTE: {{featured_image}} (the opportunity "Image" photo URL) is intentionally
    // NOT offered as a chip — a raw photo URL in the body/subject reads poorly. The
    // token still SUBSTITUTES in personalizeMessage so any message saved before it
    // was removed keeps resolving; it's just no longer a suggested variable. For an
    // INLINE photo in a GHL email, embed <img src="{{opportunity.image}}"> in the
    // GHL template (the Image custom field we stamp), which GHL resolves at send.
    // NOTE: {{deal_link}} is intentionally NOT offered as a chip — the deal link is
    // injected by the GHL automation itself (via the per-buyer opportunity custom
    // field), not the message template. The token still SUBSTITUTES in
    // personalizeMessage below so any message saved before it was removed keeps
    // resolving; it's just no longer a suggested variable.
];

// Tokens offered for the per-deal email SUBJECT line. A subject is a short
// single line, so the link-style tokens ({{featured_image}}, {{deal_link}}) are
// deliberately excluded — a URL has no place in a subject. All substitute via
// the same personalizeMessage, so anything here works in the body too.
export const SUBJECT_TOKENS = [
    { label: 'First name', token: '{{first_name}}' },
    { label: 'Last name', token: '{{last_name}}' },
    { label: 'Street address', token: '{{street_address}}' },
    { label: 'Street name', token: '{{street_name}}' },
    { label: 'Zip', token: '{{zip}}' },
    { label: 'Price', token: '{{price}}' },
    { label: 'Beds', token: '{{beds}}' },
    { label: 'Baths', token: '{{baths}}' },
    { label: 'Sq ft', token: '{{sqft}}' },
    { label: 'Year built', token: '{{year_built}}' },
];

// Email subject cap. Short by design (a single line); the send path caps
// defensively too. KEEP IN SYNC with functions/createGHLOpportunity.js and
// functions/mcpAPI (SUBJECT_MAX).
export const SUBJECT_MAX = 150;

// Sample buyer name for the composer preview, so the operator sees how a sent
// message reads with a real first/last name filled in.
export const PREVIEW_SAMPLE = { firstName: 'Alex', lastName: 'Rivera' };

// Whitespace/case-tolerant {{deal_link}} grammar — matches the substitution
// regex below, so any spelling that resolves at send time ('{{ deal_link }}',
// '{{DEAL_LINK}}') also counts as "contains the deal link" in the composer's
// warning. KEEP IN SYNC with functions/utils/personalizeMessage.js
// DEAL_LINK_TOKEN_RE (src/ cannot import functions/). Non-global so .test isn't
// stateful.
export const DEAL_LINK_TOKEN_RE = /\{\{\s*deal_link\s*\}\}/i;

function formatPrice(n) {
    return Number(n) > 0
        ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
        : '';
}

// Beds/baths/year token value — a plain number as a string, or '' when unknown.
// Bath counts can be fractional (e.g. 2.5), so no rounding.
function formatCount(n) {
    return (n === null || n === undefined || n === '' || isNaN(Number(n))) ? '' : String(n);
}

// Square footage token value — thousands-separated (e.g. "1,850"), or '' when unknown.
function formatSqft(n) {
    return (n === null || n === undefined || n === '' || isNaN(Number(n))) ? '' : Number(n).toLocaleString('en-US');
}

/**
 * Deal-level token values derived from the lead context. SINGLE SOURCE OF TRUTH
 * used by both the send payload (crmProviderConfigs) and the composer preview
 * (LeadBuyerMatches) so they can never disagree. For a portfolio the street/price
 * tokens reflect the portfolio (combined price + package name), matching the
 * opportunity's name/monetaryValue.
 *
 * `dealLinkBase` is the buyer-less deal URL (…/deals/{slug}/{leadId}); the backend
 * appends /{buyerId} per buyer at send time to produce each buyer's tracked link.
 * Requires `origin` + `companySlug` on the context (callers pass them).
 */
export function buildMessageVars(context) {
    const { lead, calculatorInputs, isPortfolio, portfolio, portfolioLeads, companySlug, origin, displayImageUrl } = context || {};
    const dealLinkBase = origin && companySlug && lead?.leadId
        ? `${origin}/deals/${companySlug}/${lead.leadId}`
        : '';
    if (isPortfolio && portfolio) {
        const combined = portfolio.combinedAskingPrice
            || (portfolioLeads || []).reduce((sum, pl) => sum + (Number(pl.currentAskingPrice) || 0), 0);
        const count = portfolio.leadCount || (portfolioLeads || []).length;
        return {
            streetAddress: `${portfolio.name || 'Portfolio'} (${count} properties)`,
            // A package has no single street, so the short street handle stays blank.
            streetName: '',
            // A package's members can span ZIPs, so leave {{zip}} blank there.
            zip: '',
            price: formatPrice(combined),
            // A package's members differ in size, so there is no single bed/bath/
            // sqft/year value — leave the tokens blank rather than pick one member's.
            beds: '',
            baths: '',
            sqft: '',
            yearBuilt: '',
            // Same package photo the opportunity's "Image" custom field gets.
            featuredImage: pickPortfolioImage(portfolioLeads) || '',
            dealLinkBase,
        };
    }
    const characteristics = lead?.verifiedCharacteristics || {};
    return {
        streetAddress: lead?.address?.streetAddress || '',
        // Short colloquial street handle (no number/unit/directional/suffix), LLM-
        // derived on ingest. Blank when the lead was ingested before it existed.
        streetName: lead?.address?.colloquialStreet || '',
        zip: lead?.address?.zipcode || '',
        price: formatPrice(calculatorInputs?.purchasePrice),
        beds: formatCount(characteristics.bedrooms),
        baths: formatCount(characteristics.bathrooms),
        sqft: formatSqft(characteristics.livingSquareFeet),
        yearBuilt: formatCount(characteristics.yearBuilt),
        // Same photo the opportunity's "Image" custom field gets — including the
        // caller's gallery hero when it supplied one (see buildOpportunityCustomFields),
        // so the composer preview can never show a different photo than the send.
        featuredImage: (typeof displayImageUrl === 'string' && displayImageUrl) || pickFeaturedImage(lead) || '',
        dealLinkBase,
    };
}

/**
 * Replace {{first_name}} / {{last_name}} / {{street_address}} / {{street_name}} /
 * {{zip}} / {{price}} / {{beds}} / {{baths}} / {{sqft}} / {{year_built}} /
 * {{featured_image}} / {{deal_link}} in a message template. Missing first name falls back to "there"
 * so a greeting never reads "Hi ,". Other missing values become empty strings.
 * `dealLink` should already be the fully-built per-buyer URL.
 */
export function personalizeMessage(template, vars) {
    const v = vars || {};
    const firstName = v.firstName && String(v.firstName).trim() ? String(v.firstName).trim() : 'there';
    const lastName = v.lastName != null ? String(v.lastName).trim() : '';
    const streetAddress = v.streetAddress != null ? String(v.streetAddress) : '';
    const streetName = v.streetName != null ? String(v.streetName) : '';
    const zip = v.zip != null ? String(v.zip) : '';
    const price = v.price != null ? String(v.price) : '';
    const beds = v.beds != null ? String(v.beds) : '';
    const baths = v.baths != null ? String(v.baths) : '';
    const sqft = v.sqft != null ? String(v.sqft) : '';
    const yearBuilt = v.yearBuilt != null ? String(v.yearBuilt) : '';
    const featuredImage = v.featuredImage != null ? String(v.featuredImage) : '';
    const dealLink = v.dealLink != null ? String(v.dealLink) : '';
    // Function replacements (not string) so a value containing $ (e.g. a price
    // "$250,000", or a stray "$&"/"$1") is inserted literally, not treated as a
    // replacement pattern.
    return (typeof template === 'string' ? template : '')
        .replace(/\{\{\s*first_name\s*\}\}/gi, () => firstName)
        .replace(/\{\{\s*last_name\s*\}\}/gi, () => lastName)
        .replace(/\{\{\s*street_address\s*\}\}/gi, () => streetAddress)
        .replace(/\{\{\s*street_name\s*\}\}/gi, () => streetName)
        .replace(/\{\{\s*zip\s*\}\}/gi, () => zip)
        .replace(/\{\{\s*price\s*\}\}/gi, () => price)
        .replace(/\{\{\s*beds\s*\}\}/gi, () => beds)
        .replace(/\{\{\s*baths\s*\}\}/gi, () => baths)
        .replace(/\{\{\s*sqft\s*\}\}/gi, () => sqft)
        .replace(/\{\{\s*year_built\s*\}\}/gi, () => yearBuilt)
        .replace(/\{\{\s*featured_image\s*\}\}/gi, () => featuredImage)
        .replace(/\{\{\s*deal_link\s*\}\}/gi, () => dealLink);
}
